// Minimal named-event SSE parser. Mirrors the shape the backend
// writes (see server/routes/agent.js::sseWrite): each frame is
//   event: <name>\ndata: <json>\n\n
//
// The DOM's EventSource can't be used here because the endpoint is
// POST-with-body (EventSource is GET-only). We drive it via fetch()
// + response.body.getReader() + this parser.

import type { AgentEvent } from './types';

type NamedFrame = { event: string; data: string };

/**
 * Parse an ReadableStreamDefaultReader<Uint8Array> into named SSE
 * frames. Yields one frame per `\n\n`-terminated event. Handles
 * partial chunks across TCP boundaries by keeping a rolling buffer.
 */
async function* readFrames(reader: ReadableStreamDefaultReader<Uint8Array>): AsyncGenerator<NamedFrame> {
  const decoder = new TextDecoder();
  let buffer = '';
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let boundary;
    while ((boundary = buffer.indexOf('\n\n')) !== -1) {
      const rawEvent = buffer.slice(0, boundary);
      buffer = buffer.slice(boundary + 2);
      const lines = rawEvent.split(/\r?\n/);
      let eventName: string | null = null;
      const dataLines: string[] = [];
      for (const line of lines) {
        if (line.startsWith('event:')) eventName = line.slice(6).trim();
        else if (line.startsWith('data:')) dataLines.push(line.slice(5).trimStart());
        // Ignore comment lines (":..." and blank lines).
      }
      if (!eventName || !dataLines.length) continue;
      yield { event: eventName, data: dataLines.join('\n') };
    }
  }
}

/**
 * Convert a raw NamedFrame to a typed AgentEvent. Unknown event names
 * return null so the consumer can skip them without a hard failure —
 * the backend may add events; older clients should degrade.
 */
function toAgentEvent(frame: NamedFrame): AgentEvent | null {
  let data: any;
  try { data = JSON.parse(frame.data); }
  catch { return null; }
  switch (frame.event) {
    case 'iteration':          return { type: 'iteration',          n: data.n, rescue: data.rescue };
    case 'assistant-delta':    return { type: 'assistant-delta',    text: data.text };
    case 'assistant-thinking-delta': return { type: 'assistant-thinking-delta', text: data.text };
    case 'tool-use-start':     return { type: 'tool-use-start',     toolCallId: data.toolCallId, toolName: data.toolName };
    case 'tool-use-complete':  return { type: 'tool-use-complete',  toolCallId: data.toolCallId, toolName: data.toolName, args: data.args };
    case 'tool-result':        return { type: 'tool-result',        toolCallId: data.toolCallId, result: data.result };
    case 'proposed-action':    return { type: 'proposed-action',    toolCallId: data.toolCallId, toolName: data.toolName, args: data.args, tier: data.tier };
    case 'plan-proposed':      return { type: 'plan-proposed',      toolCallId: data.toolCallId, toolName: data.toolName, plan: data.plan };
    case 'workflow-progress':  return { type: 'workflow-progress',  ...data };
    case 'spend-guard-block':  return { type: 'spend-guard-block',  ...data };
    case 'tier3-phrase-block': return { type: 'tier3-phrase-block', ...data };
    case 'done':               return { type: 'done',               stop_reason: data.stop_reason, iterations: data.iterations, model: data.model };
    case 'error':              return { type: 'error',              error: data.error };
    default:                   return null;
  }
}

/**
 * Given a fetch Response with a text/event-stream body, yield typed
 * AgentEvents until the stream ends. Signals aborted via the same
 * pattern the browser uses when the caller cancels — silently ends.
 */
export async function* readAgentEvents(response: Response): AsyncGenerator<AgentEvent> {
  const body = response.body;
  if (!body) return;
  const reader = body.getReader();
  try {
    for await (const frame of readFrames(reader)) {
      const evt = toAgentEvent(frame);
      if (evt) yield evt;
    }
  } finally {
    try { reader.releaseLock(); } catch { /* noop */ }
  }
}
